/**
 * Device Fingerprint Utility
 *
 * Generates a stable, privacy-respecting device identifier used by the
 * backend's new-device verification flow. The fingerprint is derived from
 * browser/OS characteristics that are stable across sessions but differ
 * between devices/browsers. It does NOT collect personally identifying
 * information and is NOT shared with third parties.
 *
 * The identifier is persisted in localStorage so the same device reports
 * the same value on every login, allowing the backend to recognize it.
 */

const STORAGE_KEY = 'ospito_device_id';

// Simple, dependency-free hash (FNV-1a 32-bit) — good enough for a fingerprint
const fnv1a = (str) => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  // Convert to unsigned hex
  return (hash >>> 0).toString(16).padStart(8, '0');
};

const collectSignals = () => {
  const nav = navigator || {};
  const screen = window.screen || {};
  const signals = [
    nav.userAgent || '',
    nav.language || '',
    (nav.languages || []).join(','),
    String(nav.platform || ''),
    String(nav.hardwareConcurrency || ''),
    String(nav.maxTouchPoints || 0),
    String(screen.width || 0),
    String(screen.height || 0),
    String(screen.colorDepth || 0),
    String(window.devicePixelRatio || 1),
    String(nav.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || ''),
    String(nav.cookieEnabled || ''),
    String(nav.doNotTrack || ''),
  ];
  return signals.join('|');
};

/**
 * Return a stable device id for this browser/device.
 * Once generated, it's cached in localStorage and reused.
 */
export const getDeviceId = () => {
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing && existing.length >= 16) return existing;
  } catch (e) { /* localStorage may be unavailable */ }

  // Generate a new fingerprint: hash of stable signals + random suffix for uniqueness
  const signals = collectSignals();
  const random = Math.random().toString(36).slice(2) + Date.now().toString(36);
  const fingerprint = `dev_${fnv1a(signals)}_${fnv1a(random)}${fnv1a(signals + random)}`;

  try {
    localStorage.setItem(STORAGE_KEY, fingerprint);
  } catch (e) { /* ignore */ }

  return fingerprint;
};

export default getDeviceId;
